import React, { useEffect, useMemo, useRef, useState } from "react";
import { IMaskInput } from "react-imask";
import {
  addDays,
  addMonths,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import ru from "date-fns/locale/ru";
import { formatDate, parseDate } from "../utils";
import "./DatePicker.css";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const CALENDAR_DAYS = 42;

const getMonthTitle = (month) => {
  const label = format(month, "LLLL yyyy", { locale: ru });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

/**
 * Одиночная дата: ручной ввод ДД.ММ.ГГГГ или выбор в календаре.
 * value / onChange — строка "ДД.ММ.ГГГГ" ("" — не выбрана).
 * highlight — { from, to } строками: подсвечивает допустимый период в календаре.
 */
function DatePicker({ value, onChange, invalid = false, highlight, compact = false, ariaLabel }) {
  const [text, setText] = useState(value || "");
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(parseDate(value) || new Date()));
  const containerRef = useRef(null);

  useEffect(() => {
    setText(value || "");
  }, [value]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setIsOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape, true);
    };
  }, [isOpen]);

  const selected = parseDate(value);
  const range = useMemo(() => {
    const from = parseDate(highlight?.from);
    const to = parseDate(highlight?.to);
    return from && to && from <= to ? { start: from, end: to } : null;
  }, [highlight?.from, highlight?.to]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
    return Array.from({ length: CALENDAR_DAYS }, (_, i) => addDays(start, i));
  }, [viewMonth]);

  const handleAccept = (next) => {
    if (next === text) return;
    setText(next);
    if (!next) {
      if (value) onChange("");
      return;
    }
    const parsed = parseDate(next);
    if (parsed) {
      onChange(next);
      setViewMonth(startOfMonth(parsed));
    }
  };

  const handleBlur = () => {
    if (text && !parseDate(text)) setText(value || "");
  };

  const toggle = () => {
    if (!isOpen) setViewMonth(startOfMonth(selected || range?.start || new Date()));
    setIsOpen((prev) => !prev);
  };

  const pickDay = (day) => {
    onChange(formatDate(day));
    setIsOpen(false);
  };

  const getDayClassName = (day) => {
    const classes = ["rr-dp-day"];
    if (!isSameMonth(day, viewMonth)) classes.push("is-outside");
    if (isToday(day)) classes.push("is-today");
    if (range && isWithinInterval(day, range)) classes.push("is-in-range");
    if (selected && isSameDay(day, selected)) classes.push("is-selected");
    return classes.join(" ");
  };

  const isTextInvalid = text.length === 10 && !parseDate(text);

  return (
    <div className={`rr-dp${compact ? " rr-dp--compact" : ""}`} ref={containerRef}>
      <div
        className={`rr-dp__control${isOpen ? " is-open" : ""}${
          invalid || isTextInvalid ? " is-invalid" : ""
        }`}
      >
        <button type="button" className="rr-dp__icon" onClick={toggle} tabIndex={-1} aria-hidden>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="3" width="12" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M2 6.5H14M5.5 2V4M10.5 2V4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </button>
        <IMaskInput
          className="rr-dp__input"
          mask="00.00.0000"
          value={text}
          unmask={false}
          onAccept={handleAccept}
          onBlur={handleBlur}
          placeholder="00.00.0000"
          inputMode="numeric"
          aria-label={ariaLabel}
        />
        <button
          type="button"
          className="rr-dp__chevron"
          onClick={toggle}
          aria-label="Открыть календарь"
          aria-expanded={isOpen}
        >
          <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
            <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="rr-dp__popover" role="dialog" aria-label="Выбор даты">
          <div className="rr-dp__header">
            <button type="button" onClick={() => setViewMonth((m) => addMonths(m, -1))} aria-label="Предыдущий месяц">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <span className="rr-dp__title">{getMonthTitle(viewMonth)}</span>
            <button type="button" onClick={() => setViewMonth((m) => addMonths(m, 1))} aria-label="Следующий месяц">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          <div className="rr-dp__weekdays">
            {WEEKDAYS.map((weekday, index) => (
              <span key={weekday} className={index > 4 ? "is-weekend" : undefined}>
                {weekday}
              </span>
            ))}
          </div>
          <div className="rr-dp__grid">
            {days.map((day) => (
              <button
                key={day.toISOString()}
                type="button"
                className={getDayClassName(day)}
                onClick={() => pickDay(day)}
                aria-label={format(day, "d MMMM yyyy", { locale: ru })}
              >
                {format(day, "d")}
              </button>
            ))}
          </div>
          <div className="rr-dp__footer">
            <button type="button" onClick={() => pickDay(new Date())}>
              Сегодня
            </button>
            {range && <span className="rr-dp__legend">Период признания</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;
